import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { money, dateFmt, formatErr } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
    ArrowLeft, Loader2, ScanLine, Save, ShieldCheck, Send, Trash2, Plus,
    CheckCircle2, AlertTriangle, FileText,
} from "lucide-react";

const confBadge = (c) => {
    if (c == null) return null;
    const pct = Math.round(c * 100);
    const cls = c >= 0.9 ? "bg-emerald-100 text-emerald-800 border-emerald-300"
        : c >= 0.7 ? "bg-amber-100 text-amber-800 border-amber-300"
        : "bg-rose-100 text-rose-800 border-rose-300 animate-pulse";
    return <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-mono ${cls}`}>{pct}%</span>;
};

export default function InvoiceDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [inv, setInv] = useState(null);
    const [gls, setGls] = useState([]);
    const [pos, setPos] = useState([]);
    const [linkPo, setLinkPo] = useState("");
    const [busy, setBusy] = useState("");
    const [fileUrl, setFileUrl] = useState(null);

    const load = () => api.get(`/invoices/${id}`).then((r) => setInv(r.data));
    useEffect(() => {
        load().catch(() => toast.error("Invoice not found"));
        api.get("/gl-accounts").then((r) => setGls(r.data)).catch(() => {});
        api.get("/purchase-orders").then((r) => setPos(r.data)).catch(() => {});
        api.get(`/invoices/${id}/file`, { responseType: "blob" })
            .then((res) => setFileUrl({ url: URL.createObjectURL(res.data), type: res.data.type }))
            .catch(() => {});
    }, [id]);

    if (!inv) return <div className="grid place-items-center py-20 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>;

    const setHeader = (k, v) => setInv((s) => ({ ...s, [k]: v }));
    const setLine = (i, k, v) => setInv((s) => ({ ...s, line_items: s.line_items.map((l, idx) => idx === i ? { ...l, [k]: v, ...(k === "quantity" || k === "unit_price" ? { amount: (k === "quantity" ? Number(v) : Number(l.quantity)) * (k === "unit_price" ? Number(v) : Number(l.unit_price)) } : {}) } : l) }));
    const addLine = () => setInv((s) => ({ ...s, line_items: [...s.line_items, { description: "", quantity: 1, unit_price: 0, amount: 0, gl_account: "" }] }));
    const delLine = (i) => setInv((s) => ({ ...s, line_items: s.line_items.filter((_, idx) => idx !== i) }));

    const subtotal = inv.line_items.reduce((sum, l) => sum + Number(l.amount || 0), 0);

    const extract = async () => {
        setBusy("extract");
        try {
            const { data } = await api.post(`/invoices/${id}/extract`);
            setInv(data);
            toast.success("AI extracted the invoice — please review the line items");
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    const save = async () => {
        setBusy("save");
        try {
            const body = {
                vendor_name: inv.vendor_name, invoice_number: inv.invoice_number,
                invoice_date: inv.invoice_date, due_date: inv.due_date, po_number: inv.po_number,
                tax: Number(inv.tax || 0),
                line_items: inv.line_items.map((l) => ({
                    description: l.description, quantity: Number(l.quantity || 0),
                    unit_price: Number(l.unit_price || 0), amount: Number(l.amount || 0),
                    gl_account: l.gl_account || null, confidence: l.confidence,
                })),
            };
            const { data } = await api.put(`/invoices/${id}`, body);
            setInv(data);
            toast.success("Saved");
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    const linkToPo = async (poId) => {
        setBusy("link");
        try {
            const { data } = await api.post(`/invoices/${id}/link-po`, { po_id: poId });
            setInv(data);
            setLinkPo("");
            toast.success(`Linked to ${data.po_number}`);
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    const runMatch = async () => {
        setBusy("match");
        try {
            const { data } = await api.post(`/invoices/${id}/match`);
            await load();
            data.matched ? toast.success("3-way match passed") : toast.warning("Match found issues — review");
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    const post = async () => {
        setBusy("post");
        try {
            const { data } = await api.post(`/invoices/${id}/post-veracross`);
            setInv(data);
            toast.success(`Posted to Veracross — ${data.veracross?.veracross_invoice_id}`);
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    const posted = inv.status === "posted";

    return (
        <div className="space-y-5 pe-fade-up" data-testid="invoice-detail">
            <button onClick={() => navigate("/app/invoices")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand-emerald">
                <ArrowLeft className="h-4 w-4" /> All invoices
            </button>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-semibold tracking-tight text-brand-emerald">{inv.original_filename}</h1>
                    <StatusBadge status={inv.status} testid="invoice-status" />
                </div>
                <div className="flex flex-wrap gap-2">
                    {inv.status === "uploaded" && (
                        <Button disabled={busy} onClick={extract} className="bg-brand-teal hover:bg-sky-600 rounded-lg" data-testid="extract-button">
                            {busy === "extract" ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Scanning…</> : <><ScanLine className="h-4 w-4 mr-1" /> Extract with AI</>}
                        </Button>
                    )}
                    {!posted && inv.status !== "uploaded" && (
                        <>
                            <Button disabled={busy} variant="outline" onClick={save} className="rounded-lg" data-testid="save-invoice-button">
                                {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
                            </Button>
                            <Button disabled={busy} variant="outline" onClick={runMatch} className="rounded-lg" data-testid="match-button">
                                {busy === "match" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-1" /> 3-Way Match</>}
                            </Button>
                            <Button disabled={busy} onClick={post} className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="post-veracross-button">
                                {busy === "post" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Post to Veracross</>}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {inv.status === "uploaded" && (
                <div className="bg-indigo-50 border border-indigo-200 text-indigo-800 rounded-xl p-4 text-sm flex items-center gap-2">
                    <ScanLine className="h-4 w-4" /> This invoice hasn't been scanned yet. Click <b>Extract with AI</b> to read the line items.
                </div>
            )}

            {inv.match && (
                <div className={`rounded-xl p-4 text-sm border ${inv.match.matched ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="match-result">
                    <div className="flex items-center gap-2 font-medium">
                        {inv.match.matched ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                        {inv.match.matched ? "3-Way match passed" : "Match exceptions found"}
                        {inv.match.po_total != null && <span className="font-mono text-xs ml-2">PO {money(inv.match.po_total)} vs Invoice {money(inv.match.invoice_total)}</span>}
                    </div>
                    {inv.match.issues?.length > 0 && (
                        <ul className="mt-2 list-disc pl-5 space-y-0.5">{inv.match.issues.map((x, i) => <li key={i}>{x}</li>)}</ul>
                    )}
                </div>
            )}

            {posted && inv.veracross && (
                <div className="bg-sky-50 border border-sky-200 text-sky-900 rounded-xl p-4 text-sm" data-testid="veracross-result">
                    <div className="font-medium flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Posted to Veracross Accounting <span className="text-xs bg-sky-200/60 px-1.5 py-0.5 rounded">MOCKED</span></div>
                    <div className="mt-1 font-mono text-xs">Veracross Invoice ID: {inv.veracross.veracross_invoice_id} · {dateFmt(inv.veracross.posted_at)}</div>
                </div>
            )}

            <div className="grid lg:grid-cols-2 gap-5">
                {/* Document preview */}
                <div className="bg-white border border-border rounded-xl overflow-hidden lg:sticky lg:top-20 self-start">
                    <div className="px-4 py-3 border-b border-border text-sm font-semibold text-brand-emerald flex items-center gap-2"><FileText className="h-4 w-4" /> Source Document</div>
                    <div className="h-[520px] bg-secondary/40 grid place-items-center overflow-auto pe-scroll">
                        {!fileUrl ? <Loader2 className="h-5 w-5 animate-spin text-brand-emerald" />
                            : fileUrl.type?.includes("pdf") ? <iframe title="invoice" src={fileUrl.url} className="w-full h-full" />
                            : <img src={fileUrl.url} alt="invoice" className="max-w-full" />}
                    </div>
                </div>

                {/* Extracted data */}
                <div className="space-y-4">
                    <div className="bg-white border border-border rounded-xl p-5 grid sm:grid-cols-2 gap-3">
                        <div><Label className="text-xs">Vendor</Label><Input disabled={posted} value={inv.vendor_name || ""} onChange={(e) => setHeader("vendor_name", e.target.value)} className="mt-1" data-testid="inv-vendor" /></div>
                        <div><Label className="text-xs">Invoice #</Label><Input disabled={posted} value={inv.invoice_number || ""} onChange={(e) => setHeader("invoice_number", e.target.value)} className="mt-1 font-mono" data-testid="inv-number" /></div>
                        <div><Label className="text-xs">Invoice date</Label><Input disabled={posted} value={inv.invoice_date || ""} onChange={(e) => setHeader("invoice_date", e.target.value)} placeholder="YYYY-MM-DD" className="mt-1" data-testid="inv-date" /></div>
                        <div><Label className="text-xs">PO Number</Label><Input disabled={posted} value={inv.po_number || ""} onChange={(e) => setHeader("po_number", e.target.value)} className="mt-1 font-mono" data-testid="inv-po" /></div>
                    </div>

                    {!posted && (
                        <div className="bg-white border border-border rounded-xl p-5 space-y-3" data-testid="po-link-card">
                            <div className="flex items-center gap-2 text-sm font-semibold text-brand-emerald"><ShieldCheck className="h-4 w-4" /> Purchase Order</div>
                            {inv.po_id ? (
                                <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="po-linked">
                                    <CheckCircle2 className="h-4 w-4" /> Linked to <span className="font-mono">{inv.po_number}</span>
                                </div>
                            ) : (
                                <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="po-unlinked">
                                    <AlertTriangle className="h-4 w-4" /> No purchase order linked. Choose one to enable the 3-way match.
                                </div>
                            )}
                            <div className="flex items-center gap-2">
                                <Select value={linkPo} onValueChange={setLinkPo}>
                                    <SelectTrigger className="font-mono text-xs h-9" data-testid="po-link-select"><SelectValue placeholder="Choose a purchase order…" /></SelectTrigger>
                                    <SelectContent>
                                        {pos.map((po) => <SelectItem key={po.id} value={po.id} className="font-mono text-xs">{po.po_number} · {po.vendor_name} · {money(po.total)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <Button disabled={busy || !linkPo} onClick={() => linkToPo(linkPo)} variant="outline" className="rounded-lg shrink-0" data-testid="link-po-button">
                                    {busy === "link" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Link"}
                                </Button>
                            </div>
                        </div>
                    )}

                    <div className="bg-white border border-border rounded-xl overflow-hidden">
                        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                            <span className="text-sm font-semibold text-brand-emerald">Line Items</span>
                            {!posted && <Button variant="ghost" size="sm" onClick={addLine} data-testid="add-inv-line"><Plus className="h-4 w-4 mr-1" /> Add</Button>}
                        </div>
                        <div className="p-3 space-y-2 max-h-[360px] overflow-auto pe-scroll">
                            {inv.line_items.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No line items yet.</div>}
                            {inv.line_items.map((l, i) => (
                                <div key={i} className="border border-border rounded-lg p-2.5" data-testid={`inv-line-${i}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Input disabled={posted} value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} placeholder="Description" className="text-sm" data-testid={`inv-line-desc-${i}`} />
                                        {confBadge(l.confidence)}
                                        {!posted && <Button variant="ghost" size="icon" className="text-rose-600 shrink-0" onClick={() => delLine(i)}><Trash2 className="h-4 w-4" /></Button>}
                                    </div>
                                    <div className="grid grid-cols-12 gap-2 items-center">
                                        <div className="col-span-5">
                                            <Select disabled={posted} value={l.gl_account || ""} onValueChange={(v) => setLine(i, "gl_account", v)}>
                                                <SelectTrigger className={`font-mono text-xs h-9 ${!l.gl_account ? "border-rose-300" : ""}`} data-testid={`inv-line-gl-${i}`}><SelectValue placeholder="GL account" /></SelectTrigger>
                                                <SelectContent>
                                                    {gls.map((g) => <SelectItem key={g.gl_account_number} value={g.gl_account_number} className="font-mono text-xs">{g.gl_account_number} · {g.name}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <Input disabled={posted} type="number" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className="col-span-2 h-9 text-sm" data-testid={`inv-line-qty-${i}`} />
                                        <Input disabled={posted} type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} className="col-span-3 h-9 text-sm" data-testid={`inv-line-price-${i}`} />
                                        <div className="col-span-2 text-right font-mono text-sm">{money(l.amount)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="px-4 py-3 border-t border-border flex justify-between text-sm bg-secondary/40">
                            <span className="text-muted-foreground">Subtotal {money(subtotal)} · Tax {money(inv.tax)}</span>
                            <span className="font-semibold font-mono text-brand-emerald">Total {money(subtotal + Number(inv.tax || 0))}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
